import { useEffect, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  Drawer,
  FormField,
  Input,
  MoneyInput,
  StyledSelect,
  type StyledSelectOption,
} from '@jumpstone/ui-react';
import type { Allowance } from '../../data/types';
import type {
  AllowanceDraft,
  SaveAllowanceResult,
} from '../../data/use_data_centre';
import {
  ALLOWANCE_RATE_KINDS,
  allowanceRateFormStateFromRule,
  defaultAllowanceRateFormState,
  isAllowanceRateSectionValid,
  resolveAllowanceRateDraft,
  type AllowanceRateFormState,
  type AllowanceRateKind,
} from './allowance_drawer_validation';

/** `payroll.createPayItem`/`updatePayItem`'s exact `name` cap (`z.string().min(1).max(200)`, `apps/api/src/trpc/routers/payroll.ts`) -- validated client-side too so a too-long name never round-trips to the generic save-failed error. */
const MAX_NAME_LENGTH = 200;

/** Matches `data_centre_bank_section.tsx`'s `SELECT_TRIGGER_CLASSES` -- same trigger shell used across the Data Centre's other `StyledSelect`s. */
const SELECT_TRIGGER_CLASSES =
  'nv-input flex h-9 w-full cursor-pointer items-center justify-between gap-2';

/**
 * Create/edit an allowance. `allowance` is null when adding a new one, in
 * which case the form starts blank and `onSave` receives `id: null` — the
 * real `payroll.createPayItem` assigns the actual id server-side (#22728);
 * this drawer never fabricates one client-side the way the mock-backed
 * version used to.
 *
 * `onSave` is now a real, awaited network call (`payroll.createPayItem` /
 * `payroll.updatePayItem`) that can fail — the drawer stays open and shows
 * an inline error on failure, mirroring `settings_screen.tsx`'s
 * `handle_save`/`save_failed` pattern, rather than closing optimistically.
 *
 * ── Rate type + amount (#22820) ──────────────────────────────────────────────
 *
 * This drawer used to collect ONLY a name — every allowance it created
 * silently became `rate_type: 'fixed'` with no `rate_cents` (the
 * `manager_input` fallback) regardless of what the user intended, since there
 * was no field to say otherwise. It now also collects a rate-type selector
 * (`allowance_drawer_validation.ts`'s `AllowanceRateKind` — a UI-level
 * 4-way superset of the backend's 3-way `rate_type` enum, `'manager_input'`
 * standing in for "fixed with no recorded rate") plus, for the two
 * amount-bearing kinds, a `<MoneyInput>`/percent field. `percentage`
 * (`percent_of_basic`) is included: `payroll.createPayItem`/`updatePayItem`'s
 * zod input applies no category restriction to `rate_type: 'percentage'`, and
 * `wagepro_store_data_centre.ts`'s own read-adapter docblock documents
 * `percent_of_basic` as valid for both allowances and deductions — it is not
 * deduction-only.
 *
 * No "Taxable" toggle (removed, adversarial review #22728): the mock-backed
 * version wrote it onto `is_pre_tax`, a reserved column the pay-run engine
 * never reads for any category (#26642), so that toggle silently wrote a dead field that
 * could never change the item's real taxability. The genuine mechanism
 * (`apit_treatment`) can only be set to `'regular'`/`'lumpsum'`/`null` via
 * `updatePayItem` today (`'exempt'` is rejected server-side, tracked
 * separately as #22663), so there is no reachable non-taxable state to offer
 * a control for yet — see `wagepro_store_data_centre.ts`'s module docblock.
 * The Allowances table still displays a read-only Taxable/Non-taxable badge,
 * now correctly sourced from `apit_treatment`.
 *
 * ── Untouched rate section round-trips unchanged (#22820 F1/F2) ──────────────
 *
 * `rate_touched` tracks whether the user has actually interacted with the
 * rate-type selector or the amount/percent field since the drawer opened —
 * flipped to `true` only by those two `onChange` handlers, NEVER by the
 * prefill effect that seeds `rate_form` from `allowance.rule`. A pure rename
 * (the user only edits the Name field) leaves it `false`, and
 * `handle_save` passes that through to
 * `allowance_drawer_validation.ts`'s `resolveAllowanceRateDraft` /
 * `isAllowanceRateSectionValid` so the save (a) sends the allowance's
 * ORIGINAL `rate_type`/`rate_cents`/`rate_bps` triad back unchanged instead
 * of re-deriving it from the current (merely prefilled) selector state, and
 * (b) is never blocked by a prefilled amount that happens to be zero/invalid
 * by the amount-bearing-kind rule. See `wagepro_store_data_centre.ts`'s
 * module docblock, same section header, for the full backend-corruption
 * rationale this closes.
 */
export function AllowanceDrawer({
  open,
  allowance,
  onClose,
  onSave,
}: {
  open: boolean;
  allowance: Allowance | null;
  onClose: () => void;
  onSave: (draft: AllowanceDraft) => Promise<SaveAllowanceResult>;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [name, set_name] = useState('');
  const [rate_form, set_rate_form] = useState<AllowanceRateFormState>(
    defaultAllowanceRateFormState(),
  );
  // #22820 F1/F2 -- see module docblock's "Untouched rate section round-trips
  // unchanged" section. Reset alongside `rate_form` below; flipped to `true`
  // ONLY by the rate-kind selector's and amount/percent field's own
  // `onChange` handlers further down, never by this prefill effect.
  const [rate_touched, set_rate_touched] = useState(false);
  const [saving, set_saving] = useState(false);
  const [save_failed, set_save_failed] = useState(false);

  useEffect(() => {
    set_name(allowance?.name ?? '');
    set_rate_form(
      allowance
        ? allowanceRateFormStateFromRule(allowance.rule)
        : defaultAllowanceRateFormState(),
    );
    set_rate_touched(false);
    set_save_failed(false);
  }, [allowance, open]);

  const trimmed = name.trim();
  const is_too_long = trimmed.length > MAX_NAME_LENGTH;
  const name_valid = trimmed.length > 0 && !is_too_long;
  const name_error =
    trimmed.length === 0
      ? t('data_centre.name_required')
      : is_too_long
        ? t('data_centre.name_too_long', { max: MAX_NAME_LENGTH })
        : undefined;

  const rate_valid = isAllowanceRateSectionValid(rate_form, rate_touched);
  const is_valid = name_valid && rate_valid;

  const rate_kind_options: StyledSelectOption<AllowanceRateKind>[] =
    ALLOWANCE_RATE_KINDS.map((kind) => ({
      value: kind,
      label: t(`data_centre.rate_kind_${kind}`),
    }));

  async function handle_save(): Promise<void> {
    set_saving(true);
    set_save_failed(false);
    const result = await onSave({
      id: allowance?.id ?? null,
      name: trimmed,
      rate: resolveAllowanceRateDraft(
        rate_form,
        rate_touched,
        allowance?.rate_snapshot,
      ),
    });
    set_saving(false);
    if (result.outcome === 'success') {
      onClose();
      return;
    }
    set_save_failed(true);
  }

  const rate_amount_invalid =
    !rate_valid &&
    (rate_form.rateKind === 'fixed' || rate_form.rateKind === 'per_hour');
  const rate_percent_invalid =
    !rate_valid && rate_form.rateKind === 'percentage';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        allowance === null
          ? t('data_centre.add_allowance')
          : t('data_centre.edit_allowance')
      }
    >
      <div className="flex flex-col gap-3">
        <FormField
          label={t('data_centre.allowance_name')}
          htmlFor="allowance-name"
          error={name_error}
        >
          <Input
            id="allowance-name"
            value={name}
            onChange={(event) => {
              set_name(event.target.value);
            }}
          />
        </FormField>

        <FormField label={t('data_centre.rate_type_label')}>
          <StyledSelect
            value={rate_form.rateKind}
            onChange={(kind) => {
              set_rate_form((current) => ({ ...current, rateKind: kind }));
              set_rate_touched(true);
            }}
            options={rate_kind_options}
            ariaLabel={t('data_centre.rate_type_label')}
            triggerClassName={SELECT_TRIGGER_CLASSES}
          />
        </FormField>

        {(rate_form.rateKind === 'fixed' ||
          rate_form.rateKind === 'per_hour') && (
          <FormField
            label={t(
              rate_form.rateKind === 'fixed'
                ? 'data_centre.allowance_amount_label_fixed'
                : 'data_centre.allowance_amount_label_per_hour',
            )}
            htmlFor="allowance-amount"
            error={
              rate_amount_invalid
                ? t('data_centre.allowance_amount_required')
                : undefined
            }
          >
            <MoneyInput
              id="allowance-amount"
              value={rate_form.amountCents}
              blankZero
              onChange={(cents) => {
                set_rate_form((current) => ({
                  ...current,
                  amountCents: cents,
                }));
                set_rate_touched(true);
              }}
            />
          </FormField>
        )}

        {rate_form.rateKind === 'percentage' && (
          <FormField
            label={t('data_centre.allowance_rate_percent_label')}
            htmlFor="allowance-rate-percent"
            error={
              rate_percent_invalid
                ? t('data_centre.allowance_rate_invalid')
                : undefined
            }
          >
            <Input
              id="allowance-rate-percent"
              type="text"
              inputMode="decimal"
              value={rate_form.ratePercentText}
              onChange={(event) => {
                const value = event.target.value;
                set_rate_form((current) => ({
                  ...current,
                  ratePercentText: value,
                }));
                set_rate_touched(true);
              }}
            />
          </FormField>
        )}

        {save_failed && (
          <p className="nv-help is-error" role="alert">
            {t('data_centre.save_error')}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <Button
            variant="primary"
            size="sm"
            disabled={!is_valid || saving}
            loading={saving}
            onClick={() => void handle_save()}
          >
            {t('data_centre.save_allowance')}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
